import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";

export default function useBookingGame() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, currency } = useWidget();
  const { isAuthenticated } = useAuth();
  // Deliberately NOT history-based. A host reaches this page straight from the
  // Telr hold-done screen, so navigate(-1) would drop them back onto a checkout
  // they've already completed. Their bookings list is where this page belongs.
  const goBack = () => navigate(isAuthenticated ? `/${slug}/dashboard?tab=bookings` : `/${slug}`);

  // Keyed on auth so seat names (attached server-side only for logged-in
  // viewers) appear without a manual reload after signing in.
  const [reloadKey, setReloadKey] = useState(0);
  const {
    data: booking,
    loading,
    error
  } = useFetch(() => api.getBooking(slug, bookingId), [slug, bookingId, isAuthenticated, reloadKey], { skip: !slug || !bookingId });
  const refresh = () => setReloadKey((k) => k + 1);

  const shareUrl = `${window.location.origin}/${slug}/join/${bookingId}`;
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      enqueueSnackbar("Booking link copied — share it with your friends", { variant: "success" });
    } catch (_err) {
      // Clipboard blocked (http / permissions) — surface the link itself.
      enqueueSnackbar(shareUrl, { variant: "info", autoHideDuration: 8000 });
    }
  };

  // The join route gates sign-in itself, so anonymous viewers can head there too.
  const goClaim = () => navigate(`/${slug}/join/${bookingId}`);

  // Hold checkout is a standalone (slug-less) page — stash the slug the same
  // way CheckoutView does so its "view game" links can find the way back.
  const goHold = (holdToken) => {
    sessionStorage.setItem("widget_slug", slug);
    navigate(`/checkout/telr-hold/${holdToken}`);
  };

  return { slug, currency, booking, loading, error, goBack, navigate, isAuthenticated, shareUrl, copyShareLink, goClaim, goHold, refresh };
}
